import { css } from 'astroturf';
import React, { useEffect, useState } from 'react';
import { Feed } from 'react-bytesize-icons';
import NewsletterForm from './newsletterForm';

const styles = css`
  .subscribe {
    margin-top: 1rem;
    text-align: center;
    display: flex;
    align-items: stretch;
    flex-direction: column;
    gap: 0.5rem;
  }
  .subscribe div {
    margin: 0;
    text-align: center;
    display: flex;
    gap: 1rem;

    & a {
      margin: 0;
      flex: 1 1 auto;
      background: none;
      box-shadow: initial;
      border: 1px solid;
      padding: 0.5rem;
      border-radius: 40px;
      line-height: 1;
      vertical-align: middle;
      display: flex;
      align-content: center;
      justify-content: center;
      gap: 0.5rem;
      transition: 0.25s ease-in-out background;

      &:hocus {
        background: rgb(0, 121, 177, 0.1);
      }
      &:last-child {
        color: rgb(197, 77, 0);
        &:hocus {
          background: rgb(197, 77, 0, 0.1);
        }
      }

      & svg {
        vertical-align: middle;
      }
    }
  }
`;

function getIds(items) {
  return items.reduce((acc, item) => {
    if (item.url) {
      // url has a # as first character, remove it to get the raw CSS-id
      acc.push(item.url.slice(1));
    }
    if (item.items) {
      acc.push(...getIds(item.items));
    }
    return acc;
  }, []);
}

function useActiveId(itemIds) {
  const [activeId, setActiveId] = useState(`test`);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: `70px 0% -50% 0%` }
    );
    itemIds.forEach((id) => {
      observer.observe(document.getElementById(id));
    });
    return () => {
      itemIds.forEach((id) => {
        const element = document.getElementById(id);
        if (element) {
          observer.unobserve(document.getElementById(id));
        }
      });
    };
  }, [itemIds]);
  return activeId;
}
function childHasActiveId(child, activeId) {
  if (child.url.slice(1) === activeId) {
    return true;
  }

  if (child.items?.length > 0) {
    return child.items.filter((childItem) => childHasActiveId(childItem, activeId)).length;
  }

  return false;
}

function hasActive(activeId, item, children) {
  if (activeId === item) {
    return true;
  }

  if (children) {
    return children.filter((child) => childHasActiveId(child, activeId)).length;
  }

  return false;
}

function renderItems(items, activeId) {
  return (
    <ol>
      {items.map((item) => (
        <li key={item.url} className={hasActive(activeId, item.url.slice(1), item.items) ? 'active' : undefined}>
          {item.items ? (
            <details open={hasActive(activeId, item.url.slice(1), item.items)}>
              <summary>
                <a
                  href={item.url}
                  className={hasActive(activeId, item.url.slice(1), item.items) ? 'active' : undefined}
                >
                  {item.title}
                </a>
              </summary>

              {item.items && renderItems(item.items, activeId)}
            </details>
          ) : (
            <a href={item.url} className={hasActive(activeId, item.url.slice(1)) ? 'active' : undefined}>
              {item.title}
            </a>
          )}
        </li>
      ))}
    </ol>
  );
}
function TableOfContents(props) {
  const idList = getIds(props.items);
  const activeId = useActiveId(idList);
  return (
    <>
      <div className="table-of-contents">
        <div className="contents-wrapper">
          <p>
            <strong>Table of contents</strong>
          </p>
          <a className="sr-only" href="#article">
            Skip table of contents
          </a>
          {renderItems(props.items, activeId)}
        </div>
      </div>
      <div className="blog-cta">
        <div className="contents-wrapper">
          <p>
            <strong>If you want to be kept up to date with new articles, CSS resources and tools,</strong> join our
            newsletter.
          </p>
          <NewsletterForm />

          <div className={styles.subscribe}>
            <strong>Or subscribe elsewhere:</strong>
            <div>
              <a href="https://twitter.com/intent/user?screen_name=polypane">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true" width={16} height={16}>
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>{' '}
                @Polypane
              </a>
              <a href="/rss.xml">
                <Feed width={16} height={16} color="#f60" strokeWidth={4} /> RSS
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
export default TableOfContents;
